import { AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import { m } from "~/lib/i18n";
import { ExecutionToastPill } from "./execution-toast-pill";
import { SimpleToastPill } from "./simple-toast-pill";
import type { CenterToastItem } from "./toast-view-model";

interface CenterToastStackProps {
  onDismissExecution: (operationId: string) => void;
  onDismissSimple: (id: CenterToastItem["key"]) => void;
  onExpandExecution: (operationId: string) => void;
  onHoverChange: (hovered: boolean) => void;
  isHovered: boolean;
  /** Newest first — index 0 renders frontmost (bottom of the stack). */
  items: readonly CenterToastItem[];
}

/**
 * Bottom-center toast stack (v1's center notification container) hosting both
 * execution pills and simple pills. Collapsed, only the frontmost pill shows
 * with a "+N" badge; hovering the container fans out every pill and freezes
 * auto-dismiss timers (the provider owns the timers and watches `isHovered`).
 *
 * z-index: above the shared z-50 overlay tier ON PURPOSE — v1 parity keeps
 * pills visible over unrelated modals, and suppression already hides the
 * pills that belong to whatever surface is open. The stack hides itself while
 * its own detail modal is open (see provider). No overflow-hidden here: the
 * light-mode surface ring is an outer 1px box-shadow that clipping would eat.
 */
export function CenterToastStack({
  onDismissExecution,
  onDismissSimple,
  onExpandExecution,
  onHoverChange,
  isHovered,
  items,
}: CenterToastStackProps) {
  // The container can empty out WHILE the cursor is over it — mouseleave
  // never fires then, and a latched hover flag would freeze every future
  // auto-dismiss timer.
  useEffect(() => () => onHoverChange(false), [onHoverChange]);
  useEffect(() => {
    if (items.length === 0) {
      onHoverChange(false);
    }
  }, [items.length, onHoverChange]);

  // NEVER return null on empty: unmounting would take AnimatePresence with
  // it and the last pill's exit could not play — every single-pill dismissal
  // (the common case) would hard-delete instead of sliding away. The empty
  // fixed container is zero-sized and inert.
  const visibleItems = isHovered ? items : items.slice(0, 1);

  return (
    <div
      aria-label={m.toast_execution_stack_notifications_aria_label()}
      aria-live="polite"
      className="fixed bottom-6 left-1/2 z-[60] flex -translate-x-1/2 flex-col-reverse items-center gap-2"
      data-testid="execution-toast-stack"
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      role="region"
    >
      <AnimatePresence mode="sync">
        {visibleItems.map((item, index) =>
          item.kind === "execution" ? (
            <ExecutionToastPill
              isFrontmost={index === 0}
              isStackHovered={isHovered}
              key={item.key}
              onDismiss={() => onDismissExecution(item.view.operationId)}
              onExpand={() => onExpandExecution(item.view.operationId)}
              stackTotal={items.length}
              view={item.view}
            />
          ) : (
            <SimpleToastPill
              isFrontmost={index === 0}
              isStackHovered={isHovered}
              key={item.key}
              onDismiss={() => onDismissSimple(item.key)}
              record={item.record}
              stackTotal={items.length}
            />
          ),
        )}
      </AnimatePresence>
    </div>
  );
}
